import type { Formatter } from 'react-timeago'

/**
 * Whole hours and minutes left on the incognito timer, or `null` once it has
 * lapsed — which callers render as nothing rather than as "0 minutes".
 *
 * Truncates rather than rounds at every step: these labels sit next to "Turn
 * off", so overstating what's left is the one direction that misleads.
 */
function splitTimeRemaining(msRemaining: number): { hours: number; minutes: number } | null {
  if (msRemaining <= 0) return null
  const totalMinutes = Math.floor(msRemaining / (60 * 1000))
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 }
}

/** How much longer incognito has to run, in words. For menus with room to read. */
export function formatIncognitoTimeRemaining(msRemaining: number): string | null {
  const split = splitTimeRemaining(msRemaining)
  if (!split) return null
  const { hours, minutes } = split

  const plural = (count: number, noun: string) => `${count} ${count === 1 ? noun : `${noun}s`}`

  if (hours > 0 && minutes > 0) return `${plural(hours, 'hour')} and ${plural(minutes, 'minute')}`
  if (hours > 0) return plural(hours, 'hour')
  if (minutes > 0) return plural(minutes, 'minute')
  return 'Less than a minute'
}

/** The same reading, abbreviated — for the Mini, which has no room for words. */
export function formatIncognitoTimeRemainingCompact(msRemaining: number): string | null {
  const split = splitTimeRemaining(msRemaining)
  if (!split) return null
  const { hours, minutes } = split

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h`
  if (minutes > 0) return `${minutes}m`
  return '<1m'
}

/**
 * `react-timeago` adapter for the above. Only formats future times — a past
 * date means the timer lapsed, and the default relative wording ("2 minutes
 * ago") would read as if incognito were still counting.
 */
export const incognitoTimeRemainingFormatter: Formatter = (_value, _unit, suffix, epochMilliseconds, _next, now) => {
  if (suffix !== 'from now') return null
  return formatIncognitoTimeRemaining(epochMilliseconds - now())
}

/** Compact `react-timeago` adapter, for the Mini. */
export const incognitoTimeRemainingCompactFormatter: Formatter = (
  _value,
  _unit,
  suffix,
  epochMilliseconds,
  _next,
  now,
) => {
  if (suffix !== 'from now') return null
  return formatIncognitoTimeRemainingCompact(epochMilliseconds - now())
}
